import { useTranslation } from "react-i18next";
import useMsTableColumns from "./ms-table-columns";
import { useTheme } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { formInEditModeState, mobilityStoresFiltersState, mobilityStoresState } from "../state";
import { IMsRow } from "../types";
import { deepClone } from "../../../../util/state/commonFunctions";
import { ColDef, IServerSideDatasource, FirstDataRenderedEvent, GridSizeChangedEvent } from "ag-grid-community"; // <--- Imported Event Types
import { useMutation } from "@tanstack/react-query";
import { getMobilityStores } from "../services/http.services";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import CustomLoader from "../../../../util/component/CustomLoader";

function MsTable() {
    const { t } = useTranslation(['network', 'translation']);
    const theme = useTheme();
    const columnDefs = useMsTableColumns();
    const { globalData } = useContext(GlobalContext);

    const tenants = useMemo<string[]>(() => {
        return globalData?.user.legalEntities?.map((elem: any) => elem.name) || [];
    }, [globalData]);

    const inEditMode = useRecoilValue(formInEditModeState);
    const [filters, setFilters] = useRecoilState(mobilityStoresFiltersState);
    const [rowData, setRowData] = useRecoilState(mobilityStoresState);
    const [pages, setPages] = useState<IMsRow[][]>([]);

    const [oldRowData, setOldRowData] = useState(rowData);
    const [remountTable, setRemountTable] = useState(0);

    const mobilityStoresMutation = useMutation(getMobilityStores);

    const defaultColDef = useMemo<ColDef>(() => ({
        autoHeaderHeight: true,
        wrapHeaderText: true,
        resizable: true,
        valueSetter(params) {
            const field = params.colDef.field! as keyof IMsRow
            const value = params.newValue

            const dataCopy = deepClone(rowData);
            const row = dataCopy.find((row: any) => row.id === params.data.id);
            row[field] = value

            setRowData(dataCopy);
            return true;
        },
    }), [rowData]);

    useEffect(() => {
        if (inEditMode) {
            setOldRowData(deepClone(rowData));
        }
        setRemountTable(old => old + 1);
    }, [inEditMode])

    const serverSideDatasource = useMemo<IServerSideDatasource>(() => ({
        getRows: async (params) => {
            try {
                if (!filters) {
                    params.success({ rowCount: 0, rowData: [] });
                    params.api.showNoRowsOverlay();
                    return;
                }

                const limit = (params.request.endRow !== undefined && params.request.startRow !== undefined) ? (params.request.endRow - params.request.startRow) : undefined;

                await mobilityStoresMutation.mutateAsync({
                    filter: { tenants, ...filters },
                    paramRequest: {
                        pagination: limit,
                        startIndex: params.request.startRow,
                        endIndex: params.request.endRow,
                        sortBy: params.request.sortModel[0] && {
                            field: params.request.sortModel[0].colId,
                            order: params.request.sortModel[0].sort,
                        },
                    },
                }).then((response: any) => {
                    const mobilityStores = response.mobilityStoreList;
                    mobilityStores.forEach((ms: any) => {
                        ms._select = true;
                    });
                    setPages(oldPages => {
                        const copy = [...oldPages];
                        const pageId = params.api.paginationGetCurrentPage();
                        while(pageId > copy.length) copy.push([]);
                        copy[pageId] = mobilityStores
                        return copy;
                    });
                    params.success({
                        rowData: mobilityStores,
                        rowCount: response.params.totCount,
                    })
                });
            } catch {
                params.fail();
                params.api.hideOverlay();
            }
        },
    }), [tenants, filters]);

    const handlePageChanged = useCallback((num: number) => {
        setRowData(pages[num] ?? []);
    }, [pages])

    const onFirstDataRendered = useCallback((event: FirstDataRenderedEvent) => {
        event.api.sizeColumnsToFit();
    }, []);

    const onGridSizeChanged = useCallback((event: GridSizeChangedEvent) => {
        event.api.sizeColumnsToFit();
    }, []);

    return (
        <>
            <Paper elevation={1} sx={{ p: theme.spacing(2), mb: theme.spacing(2) }}>
                <AgGridCustomTable
                    key={remountTable}
                    rowModelType="serverSide"
                    serverSideDatasource={serverSideDatasource}
                    columnDefs={columnDefs}
                    // toolbar={toolbar}
                    defaultColDef={defaultColDef}
                    singleClickEdit={true}
                    onFirstDataRendered={onFirstDataRendered}
                    onGridSizeChanged={onGridSizeChanged}
                    onPaginationChanged={(event) => handlePageChanged(event.api.paginationGetCurrentPage())}
                />
            </Paper>
            <CustomLoader loading={mobilityStoresMutation.isLoading} />
        </>
    )
}

export default MsTable